import { createSelector } from 'reselect'
import * as R from 'ramda'
import { getMonthDayYear } from 'helpers/DateUtil'
import {
  entitySelector,
  resultsSelector
} from 'containers/Entity/selectors'
import {
  isClientSelector,
} from 'containers/Role/Global/selectors'
import {
  currentRevisionSelector
} from 'containers/Project/GreenlightReport/selectors'

import {
  NAVIGATION_CATEGORY
} from './constants'
import {
  currentRevisionSelector as currentNvqualRevisionSelector
} from 'containers/Project/NvqualSection/selectors'
import {
  specNavbarCategories
} from 'containers/Project/Specifications/selectors'

const detailSelector = state => state.project.detail
const workflowSelector = state => state.project.workflow
const collapseSelector = state => state.uiState.project.view.collapse
const scrolledTop = state => state.uiState.project.view.scrolledTop



const entitySelectorFunc = (entities, { type, id }) => {
  return type && id
    ? entities[type][id]
    : undefined
}

const projectDetailsSelector = createSelector(
  entitySelector,
  detailSelector,
  entitySelectorFunc
)

const projectWorkflowSelector = createSelector(
  entitySelector,
  workflowSelector,
  entitySelectorFunc
)

const getDetailProperty = R.curry((prop, details) => details ? details[prop] : undefined)
const getWorkflowProperty = R.curry((prop, workflow) => workflow ? workflow[prop] : undefined)

const projectIdSelector = createSelector(
  projectDetailsSelector,
  getDetailProperty('id')
)

const projectNameSelector = createSelector(
  projectDetailsSelector,
  getDetailProperty('name')
)

const workflowIdSelector = createSelector(
  projectDetailsSelector,
  getDetailProperty('workflowId')
)

const productSkuSelector = createSelector(
  projectDetailsSelector,
  getDetailProperty('sku')
)

const productGpuSkuSelector = createSelector(
  projectDetailsSelector,
  getDetailProperty('gpuSku')
)

const partnerSiteNameSelector = createSelector(
  projectDetailsSelector,
  getDetailProperty('partnerSiteName')
)

const partnerSiteIdSelector = createSelector(
  projectDetailsSelector,
  getDetailProperty('partnerSiteId')
)

const brandSelector = createSelector(
  projectDetailsSelector,
  getDetailProperty('brand')
)

const segmentSelector = createSelector(
  projectDetailsSelector,
  getDetailProperty('segment')
)

const productHierarchyIdSelector = createSelector(
  projectDetailsSelector,
  getDetailProperty('productHierarchyId')
)

const productClassTypeIdSelector = createSelector(
  projectDetailsSelector,
  getDetailProperty('productClassTypeId')
)

const workflowLifecycleSelector = createSelector(
  projectWorkflowSelector,
  getDetailProperty('lifecycle')
)

const createdBySelector = createSelector(
  projectDetailsSelector,
  getDetailProperty('createdByUserName')
)

const createdAtSelector = createSelector(
  projectDetailsSelector,
  (details) => {
    const date = Date.parse(getDetailProperty('createdAt', details))
    return date ? getMonthDayYear(new Date(date)) : ''
  }
)

const restrictedSelector = createSelector(
  projectDetailsSelector,
  (projectDetails) => {
    const restricted = getDetailProperty('restricted', projectDetails)
    return restricted === 'Yes'
  }
)

const riskSelector = createSelector(
  projectDetailsSelector,
  (projectDetails) => {
    const risk = getDetailProperty('risk', projectDetails)
    return risk === 'Risk'
  }
)

const readOnlySelector = createSelector(
  projectDetailsSelector,
  (projectDetails) => {
    const readOnly = getDetailProperty('readOnly', projectDetails)
    return readOnly
  }
)

const architectureSelector = createSelector(
  projectDetailsSelector,
  getDetailProperty('gpuArchitecture')
)

const productLineSelector = createSelector(
  projectDetailsSelector,
  getDetailProperty('line')
)

const productClassSelector = createSelector(
  projectDetailsSelector,
  getDetailProperty('productClass')
)

const customerClassSelector = createSelector(
  projectDetailsSelector,
  getDetailProperty('customerClass')
)

const filterSubNavigationCategories = (filteredArr, nvqualTests) => {
  if (!nvqualTests) nvqualTests = []
  return filteredArr.map(cat => {
    switch (cat.key) {
      case 'nvqual_report': {
        const equals = R.flip(R.equals)
        const includes = R.flip(R.includes)
        const filterFunc = x => R.where({
          key: R.cond([
            [equals('summary'), R.T],
            [includes(nvqualTests.map(x => x.testName)), R.T]
          ])
        })(x)
        return {
          ...cat,
          list: nvqualTests.length == 0 ?
            null : R.filter(filterFunc)(cat.list)
        }
      }
      default: {
        return cat
      }
    }
  })
}

const navigationListSelector = createSelector(
  isClientSelector,
  currentRevisionSelector,
  segmentSelector,
  currentNvqualRevisionSelector,
  specNavbarCategories,
  (isClient, revision, segment, nvqualRev, specCategories) => {
    const equals = R.flip(R.equals)
    const filterFunc = (category) => R.where({
      key: R.cond([
        [R.both(R.always(isClient), equals('project_execution')), R.F],
        [R.both(R.always(isClient), equals('support_team')), R.F],
        [R.both(R.always(R.isNil(revision)), equals('green_light_report')), R.F],
        [R.both(R.always(isClient), equals('nvbugs')), R.F],
        [R.both(R.always(!['Enterprise', 'Automotive'].includes(segment)), equals('nvqual_report')), R.F],
        [R.both(R.always(segment != 'Enterprise'), equals('customer_program_management')), R.F],
        [R.both(R.always(isClient), equals('weekly_updates')), R.F],
        [R.T, R.T]
      ])
    })(category)
    let filteredArr = R.filter(filterFunc)(NAVIGATION_CATEGORY)

    if (segment === 'Automotive') {
      filteredArr[0]['list'] = specCategories
    }

    return filterSubNavigationCategories(
      filteredArr, nvqualRev ? nvqualRev.tests.filter(
        (test) => test.result != 'Skipped'
      ) : []
    )
  }
)

export {
  workflowIdSelector,
  projectIdSelector,
  projectNameSelector,
  brandSelector,
  productGpuSkuSelector,
  productSkuSelector,
  partnerSiteNameSelector,
  workflowLifecycleSelector,
  segmentSelector,
  collapseSelector,
  productHierarchyIdSelector,
  productClassTypeIdSelector,
  partnerSiteIdSelector,
  navigationListSelector,
  createdBySelector,
  createdAtSelector,
  restrictedSelector,
  riskSelector,
  readOnlySelector,
  architectureSelector,
  productLineSelector,
  productClassSelector,
  customerClassSelector,
  scrolledTop
}
